import { parseOneOf } from "./guards";
import { err, ok, type Result } from "./result";
import { parseHttpUrl, type HttpUrl, type HttpUrlParseError } from "./url";

declare const clerkPublishableKeyBrand: unique symbol;

const CLERK_PUBLISHABLE_KEY_ENVIRONMENTS = ["test", "live"] as const;

export type ClerkPublishableKeyEnvironment = (typeof CLERK_PUBLISHABLE_KEY_ENVIRONMENTS)[number];
export type ClerkPublishableKey = string & {
  readonly [clerkPublishableKeyBrand]: "ClerkPublishableKey";
};

export type OptionalClerkPublishableKey =
  | { readonly kind: "missing" }
  | {
    readonly kind: "configured";
    readonly key: ClerkPublishableKey;
    readonly environment: ClerkPublishableKeyEnvironment;
  };

export type ClerkPublishableKeyConfigError = {
  readonly kind: "invalidClerkPublishableKey";
  readonly name: "VITE_CLERK_PUBLISHABLE_KEY";
  readonly value: string;
  readonly reason: string;
};

export type ClerkClientConfigInput = {
  readonly rawPublishableKey: string | undefined;
  readonly hostname: string | null;
};

export type ClerkClientConfig = {
  readonly publishableKey: string;
  readonly enabled: boolean;
  readonly error: ClerkPublishableKeyConfigError | null;
};

const CLERK_CONFIG_NAME = "VITE_CLERK_PUBLISHABLE_KEY";
const CLERK_ENV_ASSIGNMENT_PREFIX_RE = /^VITE_CLERK_PUBLISHABLE_KEY\s*=\s*/;
const CLERK_PUBLISHABLE_KEY_RE = /^pk_([A-Za-z]+)_[A-Za-z0-9_-]+$/;

const clerkKeyInputValue = (rawValue: string | undefined): string =>
  (rawValue ?? "").trim().replace(CLERK_ENV_ASSIGNMENT_PREFIX_RE, "").trim();

const invalidClerkPublishableKey = (
  value: string,
  reason: string,
): ClerkPublishableKeyConfigError => ({
  kind: "invalidClerkPublishableKey",
  name: CLERK_CONFIG_NAME,
  value,
  reason,
});

const clerkPublishableKeyEnvironment = (
  value: string | undefined,
): Result<ClerkPublishableKeyEnvironment, ClerkPublishableKeyConfigError> => {
  if (value === undefined) {
    return err(invalidClerkPublishableKey("", "expected test or live environment"));
  }

  const parsed = parseOneOf(CLERK_PUBLISHABLE_KEY_ENVIRONMENTS, value);
  return parsed.ok
    ? ok(parsed.value)
    : err(invalidClerkPublishableKey(value, "expected test or live environment"));
};

const clerkPublishableKey = (
  value: string,
): Result<Extract<OptionalClerkPublishableKey, { kind: "configured" }>, ClerkPublishableKeyConfigError> => {
  const match = CLERK_PUBLISHABLE_KEY_RE.exec(value);
  if (match === null) {
    return err(invalidClerkPublishableKey(value, "expected pk_test_... or pk_live_..."));
  }

  const [, environmentRaw] = match;
  const environment = clerkPublishableKeyEnvironment(environmentRaw);
  return environment.ok
    ? ok({ kind: "configured", key: value as ClerkPublishableKey, environment: environment.value })
    : err({ ...environment.error, value });
};

export const parseOptionalClerkPublishableKey = (
  rawValue: string | undefined,
): Result<OptionalClerkPublishableKey, ClerkPublishableKeyConfigError> => {
  const value = clerkKeyInputValue(rawValue);
  return value === "" ? ok({ kind: "missing" }) : clerkPublishableKey(value);
};

export const clerkPublishableKeyConfigErrorMessage = (
  error: ClerkPublishableKeyConfigError,
): string => `${error.name} must be a Clerk publishable key (${error.reason}).`;

export const isClerkEnabledForHost = (
  config: OptionalClerkPublishableKey,
  hostname: string | null,
): boolean =>
  config.kind === "configured" && !(config.environment === "live" && isLocalHost(hostname));

export const clerkClientConfig = ({
  rawPublishableKey,
  hostname,
}: ClerkClientConfigInput): ClerkClientConfig => {
  const parsed = parseOptionalClerkPublishableKey(rawPublishableKey);
  const keyConfig: OptionalClerkPublishableKey = parsed.ok ? parsed.value : { kind: "missing" };

  return {
    publishableKey: keyConfig.kind === "configured" ? keyConfig.key : "",
    enabled: isClerkEnabledForHost(keyConfig, hostname),
    error: parsed.ok ? null : parsed.error,
  };
};

const isLocalHost = (hostname: string | null): boolean =>
  hostname === "localhost" || hostname === "127.0.0.1";

export type OptionalHttpUrl =
  | { readonly kind: "missing" }
  | { readonly kind: "configured"; readonly url: HttpUrl };

export type ConfigUrlError = {
  readonly kind: "invalidUrl";
  readonly name: string;
  readonly value: string;
  readonly reason: string;
};

export const parseOptionalHttpUrl = (
  name: string,
  rawValue: string | undefined,
): Result<OptionalHttpUrl, ConfigUrlError> => {
  const value = rawValue?.trim() ?? "";
  if (value === "") return ok({ kind: "missing" });

  const parsed = parseUrl(value);
  if (!parsed.ok) {
    return err({ kind: "invalidUrl", name, value, reason: parsed.error });
  }

  return ok({ kind: "configured", url: parsed.value });
};

export const configUrlErrorMessage = (error: ConfigUrlError): string =>
  `${error.name} must be an http:// or https:// URL (${error.reason}).`;

const parseUrl = (value: string): Result<HttpUrl, string> => {
  const parsed = parseHttpUrl(value);
  return parsed.ok ? ok(parsed.value.input) : err(configHttpUrlReason(parsed.error));
};

const configHttpUrlReason = (error: HttpUrlParseError): string => {
  switch (error.kind) {
    case "missingUrl":
      return "missing URL";
    case "invalidUrl":
      return error.reason;
    case "unsupportedProtocol":
      return `received ${error.protocol || "no protocol"}`;
  }
};
